import type { ComponentType, ReactElement } from 'react';
import type { AuthFlow } from '@shared/utils/analytics/events';
import { ARKOSE_GATE_VERIFY_OR_BYPASS_INTERACTION_IDS } from './interactions';
import { isArkoseGateVerifyOrBypassScreen } from './screenMapping';

type DescopeNext = (
  interactionId: string,
  payload?: Record<string, unknown>
) => Promise<unknown>;

type DescopeRecord = Record<string, unknown>;

type DescopeFlowState = DescopeRecord & {
  screenName?: string;
  form?: DescopeRecord;
  error?: { text?: string };
  next?: DescopeNext;
};

export interface ArkoseGateScreenProps {
  authFlow: AuthFlow;
  backHref?: string;
  onContinue: () => Promise<void>;
  errorText?: string;
}

export interface ArkoseGateVerifyOrBypassRouteScreenProps {
  screenName?: string;
  state: DescopeFlowState;
  form: DescopeRecord;
  smsAuthForm: DescopeRecord;
  loginBackHref: string;
  signupBackHref: string;
  ArkoseGateScreen: ComponentType<ArkoseGateScreenProps>;
}

const getStringField = (
  record: DescopeRecord | undefined,
  field: string
): string | undefined => {
  const value = record?.[field];
  return typeof value === 'string' && value.trim().length > 0
    ? value
    : undefined;
};

const getRecordField = (
  record: DescopeRecord | undefined,
  field: string
): DescopeRecord | undefined => {
  const value = record?.[field];
  return value && typeof value === 'object'
    ? (value as DescopeRecord)
    : undefined;
};

const getHashRoute = (): string => {
  if (typeof window === 'undefined') {
    return '';
  }

  const [routeWithoutQuery = ''] = window.location.hash.split('?');
  return routeWithoutQuery.replace(/^#/, '').replace(/\/$/, '') || '/';
};

export const getArkoseGateVerifyOrBypassAuthFlow = (
  state: DescopeRecord | undefined,
  form: DescopeRecord | undefined
): AuthFlow => {
  const stateForm = getRecordField(state, 'form');
  const smsAuthFlow =
    getStringField(stateForm, 'form.smsAuthFlow') ||
    getStringField(stateForm, 'smsAuthFlow') ||
    getStringField(form, 'form.smsAuthFlow') ||
    getStringField(form, 'smsAuthFlow');

  if (smsAuthFlow === 'signup' || smsAuthFlow === 'login') {
    return smsAuthFlow;
  }

  const hasSignupEmail =
    Boolean(getStringField(stateForm, 'email')) ||
    Boolean(getStringField(form, 'email'));
  const hasLoginIdentifier =
    Boolean(getStringField(stateForm, 'form.emailOrUsername')) ||
    Boolean(getStringField(form, 'form.emailOrUsername'));

  if (hasSignupEmail && !hasLoginIdentifier) {
    return 'signup';
  }

  return getHashRoute() === '/register' ? 'signup' : 'login';
};

export const routeArkoseGateVerifyOrBypassScreen = ({
  screenName,
  state,
  form,
  smsAuthForm,
  loginBackHref,
  signupBackHref,
  ArkoseGateScreen,
}: ArkoseGateVerifyOrBypassRouteScreenProps): ReactElement | null => {
  if (!screenName || !isArkoseGateVerifyOrBypassScreen(screenName)) {
    return null;
  }

  const authFlow = getArkoseGateVerifyOrBypassAuthFlow(state, form);
  const stateForm = getRecordField(state, 'form');
  const stateSmsAuthFlow =
    getStringField(stateForm, 'form.smsAuthFlow') ||
    getStringField(stateForm, 'smsAuthFlow');
  const usesSmsAuthForm = stateSmsAuthFlow === 'login';
  const payload = usesSmsAuthForm ? smsAuthForm : form;

  return (
    <ArkoseGateScreen
      authFlow={authFlow}
      backHref={authFlow === 'signup' ? signupBackHref : loginBackHref}
      onContinue={async () => {
        if (state.next) {
          await state.next(
            ARKOSE_GATE_VERIFY_OR_BYPASS_INTERACTION_IDS.CONTINUE,
            payload
          );
        }
      }}
      errorText={state?.error?.text}
    />
  );
};
